import React, { useState, useEffect } from "react";
import { StyleSheet, TextInput, View, ScrollView } from "react-native";
import { z } from "zod";
import SelectDropdown from "react-native-select-dropdown";
import useResponsiveScreen from "../../../hooks/useResponsiveScreen";
import CustomModal from "../../../components/common/general/CustomModal/CustomModal";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import CustomTextInput from "../../../components/common/general/CustomTextInput/CustomTextInput";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import NewCustomAxioActionModal from "../UsersTabContainer/NewCustomAxioActionModal";
import Pill from "../../../components/common/Pill/Pill";
import { CourseTypes } from "./CoursesTable";
import ImageUpload from "../../../components/common/general/ImageUpload/ImageUpload";
import { useEditCourseMutation, useUploadCourseThumbnailMutation } from "../../../graphql/generated/graphql";
import CustomSpacing from "../../../components/common/layout/CustomSpacing/CustomSpacing";
import AddCourseSecondStep from "./AddCourseSecondStep";
import CurrencyInput from "react-native-currency-input";
import { useAppConfig } from "../../../AppConfigProvider";
import AddCourseDegreesStep from "./AddCourseDegreesStep";

const modals = {
  editConfirmation: {
    title: "Success!",
    ariaLabel: "Course Added Successfully Modal",
    warningText: "The course has been Edited.",
    actionButtonText: "OK",
  },
  editError: {
    title: "Error!",
    ariaLabel: "Course Edition Error Modal",
    warningText: "You may not have permission to edit the course.",
    actionButtonText: "CLOSE",
  },
};

const courseTypeOptions = [
  {
    name: "Core",
    color: "#3E68FE",
    textColor: "#fff",
  },
  {
    name: "General",
    color: "#008383",
    textColor: "#fff",
  },
  {
    name: "Elective",
    color: "#9747FF",
    textColor: "#fff",
  },
];

export enum EditCourseModalState {
  CLOSED = "CLOSED",
  EDIT_CONFIRMATION = "EDIT_CONFIRMATION",
  EDIT_ERROR = "EDIT_ERROR",
}

const courseSchema = z.object({
  courseName: z.string().min(1, "Course name is required"),
  courseType: z.enum(["Core", "General", "Elective"]) as z.ZodType<"Core" | "General" | "Elective">,
  degrees: z.array(z.object({ id: z.string(), name: z.string() })).optional(),
  thumbnail: z.string().optional(),
  description: z.string().optional(),
  objectives: z.string().optional(),
  targetAudience: z.string().optional(),
  cost: z.number().optional(),
});

type CourseFormData = z.infer<typeof courseSchema>;

type SubmitCourseFormData = Omit<CourseFormData, "degrees"> & {
  degrees?: string[];
};

interface EditCourseModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (formData: SubmitCourseFormData) => Promise<{
    success: boolean;
  }>;
  courseData?: any;
}

const EditCourseModal: React.FC<EditCourseModalProps> = ({ visible, onClose, onSubmit, courseData }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isMedium } = useResponsiveScreen();
  const [modalState, setModalState] = useState({
    state: EditCourseModalState.CLOSED,
  });
  const { hasDegrees } = useAppConfig();
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [formStep, setFormStep] = useState<"first" | "hasDegrees" | "second">("first");

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    getValues,
    reset,
    trigger,
  } = useForm<CourseFormData>({
    resolver: zodResolver(courseSchema),
    defaultValues: {
      courseName: "",
      courseType: "Core",
      degrees: [],
      thumbnail: "",
      description: "",
      objectives: "",
      targetAudience: "",
      cost: 0,
    },
  });

  useEffect(() => {
    if (courseData) {
      console.log("courseData: ", courseData);
      reset({
        courseName: courseData.name || "",
        courseType: (["Core", "General", "Elective"].includes(courseData.courseType)
          ? courseData.courseType
          : "Core") as "Core" | "General" | "Elective",
        degrees:
          courseData.degreesHasCourse?.map((d: any) => ({
            id: d.id,
            name: d.name,
          })) || [],
        thumbnail: courseData.thumbnail || "",
        description: courseData.description || "",
        objectives: Array.isArray(courseData.objectives)
          ? courseData.objectives.join("\n")
          : courseData.objectives || "",
        targetAudience: courseData.targetAudience || "",
        cost: courseData.cost ?? 0,
      });
    }
  }, [courseData, reset]);

  const [uploadThumbnail] = useUploadCourseThumbnailMutation();

  const handleClose = () => {
    onClose();
    reset();
    setFormStep("first");
  };

  const [editCourse] = useEditCourseMutation();

  const handleFormSubmit = React.useCallback(
    async (formData: CourseFormData) => {
      try {
        setIsSubmitting(true);

        const result = await onSubmit({
          courseName: formData.courseName,
          courseType: formData.courseType,
          description: formData.description,
          objectives: formData.objectives,
          cost: formData.cost,
          degrees: formData.degrees?.map((d) => d.id),
          targetAudience: formData.targetAudience,
        });

        if (!result || !result.success) {
          setModalState({ state: EditCourseModalState.EDIT_ERROR });
          setIsSubmitting(false);
          return;
        }

        if (formData.thumbnail && courseData?.id) {
          try {
            const base64Data = formData.thumbnail.split(",")[1];
            const contentType = formData.thumbnail.split(";")[0].split(":")[1];
            const size = Math.ceil((base64Data.length * 3) / 4);

            const uploadResult = await uploadThumbnail({
              variables: {
                input: {
                  image: base64Data,
                  contentType,
                  size,
                  courseId: courseData.id,
                },
              },
            });

            console.log("[handleFormSubmit] Thumbnail upload result:", uploadResult);
          } catch (uploadError) {
            console.error("[handleFormSubmit] Thumbnail upload failed:", uploadError);
          }
        }

        handleClose();
        setModalState({ state: EditCourseModalState.EDIT_CONFIRMATION });
        setIsSubmitting(false);
      } catch (error) {
        setModalState({ state: EditCourseModalState.EDIT_ERROR });
        setIsSubmitting(false);
      }
    },
    [editCourse, courseData, uploadThumbnail, handleClose]
  );

  const isFirstStep = formStep === "first";
  const isSecondStep = formStep === "second";
  const isDegreesStep = formStep === "hasDegrees";

  const handleNext = async () => {
    if (formStep === "first") {
      const isValid = await trigger(["courseName", "courseType"]);
      if (isValid) {
        if (hasDegrees) {
          setFormStep("hasDegrees");
        } else {
          setFormStep("second");
        }
      }
    } else if (formStep === "hasDegrees") {
      setFormStep("second");
    }
  };

  const handleBackOrCancel = () => {
    if (formStep === "second") {
      if (hasDegrees) {
        setFormStep("hasDegrees");
      } else {
        setFormStep("first");
      }
    } else if (formStep === "hasDegrees") {
      setFormStep("first");
    } else {
      handleClose();
    }
  };

  return (
    <>
      <CustomModal
        visible={visible}
        onRequestClose={handleClose}
        title="Edit a Course"
        exitFill="#6B7280"
        ariaLabel="Edit course modal"
        style={{ width: isMedium ? "50%" : "90%", maxHeight: "90vh" }}
        onShow={() => {}}
        showCloseButton={true}>
        <ScrollView style={styles.modalScrollView} showsVerticalScrollIndicator={true}>
          <View style={styles.container}>
            {isFirstStep && (
              <>
                <View style={styles.formGroup}>
                  <CustomText
                    text="Course Name"
                    size="s"
                    textType="text"
                    style={{ color: "#182230" }}
                    weight="ultraBold"
                  />
                  <CustomSpacing size="s" type="vertical" />
                  <Controller
                    control={control}
                    name="courseName"
                    render={({ field: { onChange, value } }) => (
                      <CustomTextInput
                        value={value}
                        onChangeText={onChange}
                        placeholder="Understanding Design Change Types"
                        style={styles.input}
                      />
                    )}
                  />
                  {errors?.courseName?.message && (
                    <CustomText size="xs" style={{ color: "#EF4444" }} text={errors.courseName?.message} />
                  )}
                </View>

                <View style={styles.formGroup}>
                  <CustomText
                    text="Select a Course Type"
                    size="s"
                    textType="text"
                    style={{ color: "#182230" }}
                    weight="ultraBold"
                  />
                  <CustomText text="Choose from Core, General, and Elective" size="xs" style={styles.helperText} />
                  <Controller
                    control={control}
                    name="courseType"
                    render={({ field: { onChange, value } }) => (
                      <SelectDropdown
                        data={courseTypeOptions}
                        defaultValue={courseTypeOptions.find((o) => o.name === value)}
                        onSelect={(item) => onChange(item.name)}
                        buttonStyle={[styles.dropdown, errors.courseType && styles.dropdownError]}
                        buttonTextStyle={styles.dropdownText}
                        dropdownStyle={styles.dropdownList}
                        rowStyle={styles.dropdownRow}
                        rowTextStyle={styles.dropdownRowText}
                        defaultButtonText="Select course type"
                        buttonTextAfterSelection={(item) => item.name}
                        rowTextForSelection={(item) => item.name}
                        renderDropdownIcon={(isOpened) => (
                          <View style={[styles.dropdownIcon, isOpened && styles.dropdownIconUp]} />
                        )}
                        dropdownIconPosition="right"
                        renderCustomizedButtonChild={(selectedItem) => (
                          <View style={styles.dropdownButton}>
                            {selectedItem ? (
                              <>
                                <Pill
                                  leftIcon={CourseTypes[selectedItem?.name]?.icon}
                                  backgroundColor={selectedItem.color}
                                />
                                <CustomText text={selectedItem.name} size="xs" style={styles.dropdownButtonText} />
                              </>
                            ) : (
                              <CustomText text="Select course type" size="xs" style={styles.dropdownPlaceholder} />
                            )}
                          </View>
                        )}
                        renderCustomizedRowChild={(item, index) => (
                          <View style={styles.dropdownRow}>
                            <Pill leftIcon={item.icon} backgroundColor={item.color} />
                            <CustomText text={item.name} size="xs" style={styles.dropdownRowText} />
                          </View>
                        )}
                      />
                    )}
                  />
                  {errors?.courseType?.message && (
                    <CustomText size="xs" style={{ color: "#EF4444" }} text={errors.courseType?.message} />
                  )}
                </View>

                <View style={styles.formGroup}>
                  <CustomText
                    text="Upload a course thumbnail"
                    size="s"
                    textType="text"
                    style={{ color: "#182230" }}
                    weight="ultraBold"
                  />
                  <CustomText
                    text="We'll generate a thumbnail for you if you leave this blank. Recommended size: 800×450"
                    size="xs"
                    style={styles.helperText}
                  />
                  {uploadError && <CustomText text={uploadError} size="xs" style={styles.errorText} />}
                  <View style={styles.uploadContainer}>
                    <Controller
                      control={control}
                      name="thumbnail"
                      render={({ field: { onChange, value } }) => (
                        <ImageUpload
                          onChange={onChange}
                          value={value}
                          aspectRatio={[16, 9]}
                          allowsEditing={true}
                          onError={(error) => setUploadError(error.message)}
                        />
                      )}
                    />
                  </View>
                </View>

                <View style={styles.formGroup}>
                  <CustomText
                    text="Course Cost"
                    size="s"
                    textType="text"
                    style={{ color: "#182230" }}
                    weight="ultraBold"
                  />
                  <Controller
                    control={control}
                    name="cost"
                    render={({ field: { onChange, value } }) => (
                      <CurrencyInput
                        renderTextInput={(textInputProps) => (
                          <TextInput style={styles.currencyInput} {...textInputProps} />
                        )}
                        value={value}
                        onChangeValue={onChange}
                        delimiter=","
                        separator="."
                        precision={2}
                        minValue={0}
                        keyboardType="numeric"
                        placeholder="Enter amount"
                        prefix="$"
                      />
                    )}
                  />
                  {errors?.cost?.message && (
                    <CustomText size="xs" style={{ color: "#EF4444" }} text={errors.cost?.message} />
                  )}
                </View>
              </>
            )}

            {isDegreesStep && <AddCourseDegreesStep control={control} />}
            {isSecondStep && <AddCourseSecondStep control={control} />}

            <View style={styles.buttonContainer}>
              <CustomButton
                text={isFirstStep ? "Cancel" : "Back"}
                onPress={handleBackOrCancel}
                styleType="secondary"
                size="m"
                style={styles.cancelButton}
                textStyle={styles.cancelButtonText}
                leftIcon={null}
                rightIcon={null}
                disabled={false}
                textProps={{}}
              />
              {(isFirstStep || isDegreesStep) && (
                <CustomButton
                  text={"Next"}
                  onPress={handleNext}
                  styleType="primary"
                  size="m"
                  style={styles.addButton}
                  textStyle={styles.addButtonText}
                  leftIcon={null}
                  rightIcon={null}
                  disabled={false}
                  textProps={{}}
                />
              )}
              {isSecondStep && (
                <CustomButton
                  text={"Submit"}
                  onPress={handleSubmit(handleFormSubmit)}
                  styleType="primary"
                  size="m"
                  style={!isValid || isSubmitting ? { backgroundColor: "#ccc" } : styles.addButton}
                  textStyle={styles.addButtonText}
                  leftIcon={null}
                  rightIcon={null}
                  disabled={!isValid || isSubmitting}
                  textProps={{}}
                />
              )}
            </View>
          </View>
        </ScrollView>
      </CustomModal>
      <NewCustomAxioActionModal
        visible={modalState.state === EditCourseModalState.EDIT_CONFIRMATION}
        onClose={() => setModalState({ state: EditCourseModalState.CLOSED })}
        modalTexts={modals.editConfirmation}
        contextualText={getValues("courseName")}
      />
      <NewCustomAxioActionModal
        visible={modalState.state === EditCourseModalState.EDIT_ERROR}
        onClose={() => setModalState({ state: EditCourseModalState.CLOSED })}
        modalTexts={modals.editError}
      />
    </>
  );
};

const styles = StyleSheet.create({
  modalScrollView: {
    flex: 1,
    width: "100%",
  },
  container: {
    padding: 24,
  },
  formGroup: {
    marginBottom: 24,
  },
  helperText: {
    color: "#6B7280",
    marginBottom: 8,
  },
  input: {
    width: "100%",
  },
  currencyInput: {
    borderWidth: 1,
    borderColor: "#D1D5DB",
    color: "#1F2937",
    fontSize: 14,
    borderRadius: 8,
    width: "100%",
    padding: 10,
  },
  buttonContainer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    gap: 12,
    marginTop: 8,
  },
  cancelButton: {
    backgroundColor: "transparent",
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 6,
    paddingHorizontal: 16,
  },
  addButton: {
    // backgroundColor: "#4F46E5",
    borderRadius: 6,
    paddingHorizontal: 16,
  },
  cancelButtonText: {
    color: "#4B5563",
  },
  addButtonText: {
    color: "#FFFFFF",
  },
  dropdown: {
    width: "100%",
    height: 40,
    backgroundColor: "white",
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#D1D5DB",
    paddingHorizontal: 12,
  },
  dropdownError: {
    borderColor: "#EF4444",
  },
  dropdownText: {
    fontSize: 14,
    color: "#111827",
    textAlign: "left",
  },
  dropdownList: {
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#D1D5DB",
    backgroundColor: "white",
  },
  dropdownRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 16,
  },
  dropdownRowText: {
    fontSize: 14,
    color: "#111827",
  },
  dropdownIcon: {
    width: 0,
    height: 0,
    backgroundColor: "transparent",
    borderStyle: "solid",
    borderLeftWidth: 5,
    borderRightWidth: 5,
    borderTopWidth: 5,
    borderLeftColor: "transparent",
    borderRightColor: "transparent",
    borderTopColor: "#6B7280",
  },
  dropdownIconUp: {
    transform: [{ rotate: "180deg" }],
  },
  errorText: {
    color: "#EF4444",
    marginTop: 4,
  },
  dropdownButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  dropdownButtonText: {
    color: "#111827",
    fontSize: 14,
  },
  dropdownPlaceholder: {
    color: "#6B7280",
    fontSize: 14,
  },
  uploadContainer: {
    marginTop: 2,
  },
});

export default EditCourseModal;
